import {AttrsSchema, Component, ConfigOptions} from 'panel';
import copy from 'clipboard-copy';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {ButtonTheme} from 'common/components/button/constants';
import {emitTrackEvent} from 'common/util/dom';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';

import {
  commonSharingTrackProps,
  CopyLinkUISource,
  eventPrefix,
  ShareButtonUISource,
  SharingTrackArgs,
  track,
} from '../tracking';
import {createShortUrl, validateSharingAttributes} from '../utils';
import {SharingMenuLabels, SharingStatusButtonAttrs, SharingStatusButtonState} from './types';
import {template} from './template';

interface StatusButtonJsxProps {
  attrs?: SharingStatusButtonAttrs;
}

export enum CustomEvents {
  GrantFetchFailed = `failedGrantFetch`,
  GrantFetchSuccess = `successGrantFetch`,
}

export class SharingStatusButton extends Component<SharingStatusButtonState, SharingStatusButtonAttrs> {
  static get attrsSchema(): AttrsSchema<SharingStatusButtonAttrs> {
    return {
      ...super.attrsSchema,
      disabled: `boolean`,
      theme: {type: `string`, enum: Object.values(ButtonTheme), default: ButtonTheme.Secondary},
      type: `string`,
      id: `number`,
      'tooltip-text': `string`,
      'is-select': `boolean`,
      'project-id': `number`,
    };
  }

  get config(): ConfigOptions<SharingStatusButtonState> {
    return {
      template,
      defaultState: {
        error: null,
        data: null,
      },
      helpers: {
        isUnsavedReport: () => !this.attr(`id`),
        isDisabled: () =>
          this.attr(`disabled`) ||
          !validateSharingAttributes({
            projectId: this.attr(`project-id`),
            id: this.attr(`id`),
            type: this.attr(`type`),
            options: {ignoreEntityId: true},
          }),
        /** select version of share status button */
        handleMenuItemChange: async (ev) => {
          const buttonClicked = ev.detail.value;
          if (buttonClicked === SharingMenuLabels.CopyLink) {
            try {
              // @ts-ignore
              const isSafari = window.safari !== undefined;
              //@ts-ignore
              const isFirefox = typeof InstallTrigger !== `undefined`;
              if (isSafari || isFirefox) {
                /** In Safari/Firefox browser, if we have 2 awaits next to each other, the clipboard action always fails
                 * In this case, we just copy the current url. :(
                 */
                await copy(window.location.toString());
                this.track(track.shortLink(window.location.toString(), CopyLinkUISource.SharingDropdownMenu));
              } else {
                const shortUrl = await createShortUrl(window.location.toString());
                await copy(shortUrl);
                this.track(track.shortLink(shortUrl, CopyLinkUISource.SharingDropdownMenu));
              }
              addToast({message: `Link Copied!`, type: ToastType.SUCCESS});
            } catch (err) {
              addToast({message: `Something went wrong`, type: ToastType.ERROR});
              this.track(track.shortLinkCopyFailed(CopyLinkUISource.SharingDropdownMenu));
            }
          } else if (buttonClicked === SharingMenuLabels.ManageSharing) {
            this.track(track.shareButtonClick(ShareButtonUISource.ShareDropdownManageSharing));
            this.dispatchEvent(new CustomEvent(`share`, {}));
          }
        },
        handleMenuChange: (ev) => {
          if (ev.detail.isOpen) {
            const isEntitySelected = !!this.attr(`id`);
            this.track(track.shareDropdownMenuClick(this.attr(`type`), isEntitySelected));
          }
        },
        /** regular share status button - will be deprecated soon in favor of dropdown*/
        handleClick: () => {
          // When a user clicks on share button in brand new report, avoid tracking until new report is saved w/ id
          if (!this.helpers.isUnsavedReport()) {
            this.track(track.shareButtonClick(ShareButtonUISource.OldSharingButton));
          }
        },
      },
    };
  }

  track({eventName, props}: SharingTrackArgs) {
    emitTrackEvent(this, {
      eventName,
      eventPrefix,
      props: {...commonSharingTrackProps(), ...props},
    });
  }
}

export const SharingStatusButtonEl = defineAndMakeJsxFactory<StatusButtonJsxProps>(
  `mp-sharing-status-button`,
  SharingStatusButton,
);
